import { useEffect, useMemo, useState } from 'react';
import { CalendarIcon } from './CalendarIcon';
import { DateCalendarPanel } from './DateCalendarPanel';
import {
  addDays,
  clampMonthToToday,
  clampQuarterToToday,
  compareDate,
  createOptionalCalendarDate,
  formatCalendarDate,
  formatMonth,
  formatOptionalCalendarDate,
  formatQuarter,
  formatYear,
  getRangeDays,
  getToday,
  getYearGroupStart,
  isFutureMonth,
  isFutureQuarter,
  isFutureYear,
  monthLabels,
} from './dateControlUtils';
import type {
  CalendarDate,
  CalendarDateRange,
  DateControlLocale,
  YearMonth,
  YearQuarter,
} from './types';
import { useOutsideClose } from './useOutsideClose';

export type {
  CalendarDate,
  CalendarDateRange,
  DateControlLocale,
  YearMonth,
  YearQuarter,
} from './types';

interface PickerBaseProps {
  title: string;
  locale?: DateControlLocale;
  defaultOpen?: boolean;
  disableFuture?: boolean;
}

interface MonthPickerProps extends PickerBaseProps {
  value: YearMonth;
  onChange: (value: YearMonth) => void;
}

interface QuarterRangePickerProps extends PickerBaseProps {
  value: YearQuarter;
  onChange: (value: YearQuarter) => void;
}

interface YearPickerProps extends PickerBaseProps {
  value: number;
  onChange: (value: number) => void;
}

interface CalendarProps {
  value: CalendarDate;
  onChange: (value: CalendarDate) => void;
  locale?: DateControlLocale;
  ariaLabel: string;
  showActions?: boolean;
  disableFuture?: boolean;
  confirmDisabled?: boolean;
}

interface MultiCalendarProps {
  value: CalendarDateRange;
  onChange: (value: CalendarDateRange) => void;
  locale?: DateControlLocale;
  disableFuture?: boolean;
  confirmDisabled?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
}

interface DatePickerProps extends PickerBaseProps {
  value: CalendarDate;
  onChange: (value: CalendarDate) => void;
  confirmDisabled?: boolean;
}

interface MultiDatePickerProps {
  title: string;
  value: CalendarDateRange;
  onChange: (value: CalendarDateRange) => void;
  locale?: DateControlLocale;
  disableFuture?: boolean;
  confirmDisabled?: boolean;
}

const isSameDateRange = (left: CalendarDateRange, right: CalendarDateRange) =>
  Boolean(
    left.start &&
      left.end &&
      right.start &&
      right.end &&
      compareDate(left.start, right.start) === 0 &&
      compareDate(left.end, right.end) === 0,
  );

export const MonthPicker = ({
  title,
  value,
  onChange,
  locale = 'ko',
  defaultOpen = false,
  disableFuture = true,
}: MonthPickerProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [viewYear, setViewYear] = useState(value.year);
  const rootRef = useOutsideClose(isOpen, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen) {
      setViewYear(value.year);
    }
  }, [isOpen, value.year]);

  const changeYear = (amount: number) => {
    const nextYear = viewYear + amount;
    const nextValue = disableFuture
      ? clampMonthToToday({ ...value, year: nextYear })
      : { ...value, year: nextYear };

    setViewYear(nextValue.year);
    onChange(nextValue);
  };
  const today = getToday();
  const isNextYearDisabled = disableFuture && viewYear >= today.year;

  return (
    <div ref={rootRef} className={`calendar-picker${isOpen ? ' is-open' : ''}`}>
      <strong>{title}</strong>
      <button type="button" className="calendar-trigger" onClick={() => setIsOpen((current) => !current)}>
        <span>{formatMonth(value, locale)}</span>
        <CalendarIcon />
      </button>
      {isOpen && (
        <div className="calendar-picker__panel">
          <div className="calendar-picker__head">
            <button type="button" aria-label={locale === 'ko' ? '이전 년' : 'Previous year'} onClick={() => changeYear(-1)}>
              ‹
            </button>
            <strong>{viewYear}</strong>
            <button
              type="button"
              aria-label={locale === 'ko' ? '다음 년' : 'Next year'}
              disabled={isNextYearDisabled}
              onClick={() => changeYear(1)}
            >
              ›
            </button>
          </div>
          <div className="calendar-picker__grid calendar-picker__grid--month">
            {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => {
              const optionValue = { year: viewYear, month };
              const isDisabled = disableFuture && isFutureMonth(optionValue);

              return (
                <button
                  type="button"
                  className={[
                    value.year === viewYear && value.month === month ? 'is-selected' : '',
                    isDisabled ? 'is-disabled' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={isDisabled}
                  key={month}
                  onClick={() => onChange(optionValue)}
                >
                  {monthLabels[locale][month - 1]}
                </button>
              );
            })}
          </div>
          <div className="calendar-picker__footer">
            <button type="button" className="lab-button lab-button--sm" onClick={() => setIsOpen(false)}>
              {locale === 'ko' ? '취소' : 'Cancel'}
            </button>
            <button type="button" className="lab-button lab-button--sm lab-button--blue" onClick={() => setIsOpen(false)}>
              {locale === 'ko' ? '확인' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const QuarterRangePicker = ({
  title,
  value,
  onChange,
  locale = 'ko',
  defaultOpen = false,
  disableFuture = true,
}: QuarterRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [viewYear, setViewYear] = useState(value.year);
  const rootRef = useOutsideClose(isOpen, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen) {
      setViewYear(value.year);
    }
  }, [isOpen, value.year]);

  const changeYear = (amount: number) => {
    const nextYear = viewYear + amount;
    const nextValue = disableFuture
      ? clampQuarterToToday({ ...value, year: nextYear })
      : { ...value, year: nextYear };

    setViewYear(nextValue.year);
    onChange(nextValue);
  };
  const today = getToday();
  const isNextYearDisabled = disableFuture && viewYear >= today.year;

  return (
    <div ref={rootRef} className={`calendar-picker${isOpen ? ' is-open' : ''}`}>
      <strong>{title}</strong>
      <button type="button" className="calendar-trigger" onClick={() => setIsOpen((current) => !current)}>
        <span>{formatQuarter(value, locale)}</span>
        <CalendarIcon />
      </button>
      {isOpen && (
        <div className="calendar-picker__panel calendar-picker__panel--compact">
          <div className="calendar-picker__head">
            <button type="button" aria-label={locale === 'ko' ? '이전 5년' : 'Previous 5 years'} onClick={() => changeYear(-5)}>
              «
            </button>
            <button type="button" aria-label={locale === 'ko' ? '이전 년' : 'Previous year'} onClick={() => changeYear(-1)}>
              ‹
            </button>
            <strong>{viewYear}</strong>
            <button
              type="button"
              aria-label={locale === 'ko' ? '다음 년' : 'Next year'}
              disabled={isNextYearDisabled}
              onClick={() => changeYear(1)}
            >
              ›
            </button>
            <button
              type="button"
              aria-label={locale === 'ko' ? '다음 5년' : 'Next 5 years'}
              disabled={isNextYearDisabled}
              onClick={() => changeYear(5)}
            >
              »
            </button>
          </div>
          <div className="calendar-picker__grid calendar-picker__grid--quarter">
            {[1, 2, 3, 4].map((quarter) => {
              const optionValue = { year: viewYear, quarter };
              const isDisabled = disableFuture && isFutureQuarter(optionValue);

              return (
                <button
                  type="button"
                  className={[
                    value.year === viewYear && value.quarter === quarter ? 'is-selected' : '',
                    isDisabled ? 'is-disabled' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={isDisabled}
                  key={quarter}
                  onClick={() => onChange(optionValue)}
                >
                  {locale === 'ko' ? `${quarter}분기` : `Q${quarter}`}
                </button>
              );
            })}
          </div>
          <div className="calendar-picker__footer">
            <button type="button" className="lab-button lab-button--sm" onClick={() => setIsOpen(false)}>
              {locale === 'ko' ? '취소' : 'Cancel'}
            </button>
            <button type="button" className="lab-button lab-button--sm lab-button--blue" onClick={() => setIsOpen(false)}>
              {locale === 'ko' ? '확인' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const YearPicker = ({
  title,
  value,
  onChange,
  locale = 'ko',
  defaultOpen = false,
  disableFuture = true,
}: YearPickerProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [viewStartYear, setViewStartYear] = useState(getYearGroupStart(value));
  const rootRef = useOutsideClose(isOpen, () => setIsOpen(false));
  const years = useMemo(
    () => Array.from({ length: 10 }, (_, index) => viewStartYear + index),
    [viewStartYear],
  );
  const today = getToday();
  const isNextGroupDisabled = disableFuture && viewStartYear + 10 > today.year;

  useEffect(() => {
    if (!isOpen) {
      setViewStartYear(getYearGroupStart(value));
    }
  }, [isOpen, value]);

  return (
    <div ref={rootRef} className={`calendar-picker calendar-picker--wide${isOpen ? ' is-open' : ''}`}>
      <strong>{title}</strong>
      <button type="button" className="calendar-trigger" onClick={() => setIsOpen((current) => !current)}>
        <span>{formatYear(value, locale)}</span>
        <CalendarIcon />
      </button>
      {isOpen && (
        <div className="calendar-picker__panel calendar-picker__panel--wide">
          <div className="calendar-picker__head">
            <button
              type="button"
              aria-label={locale === 'ko' ? '이전 년도 묶음' : 'Previous year group'}
              onClick={() => setViewStartYear((current) => current - 10)}
            >
              ‹
            </button>
            <strong>
              {locale === 'ko'
                ? `${formatYear(viewStartYear, locale)} - ${formatYear(viewStartYear + 9, locale)}`
                : `${viewStartYear} - ${viewStartYear + 9}`}
            </strong>
            <button
              type="button"
              aria-label={locale === 'ko' ? '다음 년도 묶음' : 'Next year group'}
              disabled={isNextGroupDisabled}
              onClick={() => setViewStartYear((current) => current + 10)}
            >
              ›
            </button>
          </div>
          <div className="calendar-picker__grid calendar-picker__grid--year">
            {years.map((year) => {
              const isDisabled = disableFuture && isFutureYear(year);

              return (
                <button
                  type="button"
                  className={[
                    year === value ? 'is-selected' : '',
                    isDisabled ? 'is-disabled' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={isDisabled}
                  key={year}
                  onClick={() => onChange(year)}
                >
                  {formatYear(year, locale)}
                </button>
              );
            })}
          </div>
          <div className="calendar-picker__footer">
            <button type="button" className="lab-button lab-button--sm" onClick={() => setIsOpen(false)}>
              {locale === 'ko' ? '취소' : 'Cancel'}
            </button>
            <button type="button" className="lab-button lab-button--sm lab-button--blue" onClick={() => setIsOpen(false)}>
              {locale === 'ko' ? '확인' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const Calendar = ({
  value,
  onChange,
  locale = 'ko',
  ariaLabel,
  showActions,
  disableFuture = true,
  confirmDisabled,
}: CalendarProps) => {
  const [draftValue, setDraftValue] = useState(value);
  const [visibleMonth, setVisibleMonth] = useState<YearMonth>({
    year: value.year,
    month: value.month,
  });
  const displayedValue = showActions ? draftValue : value;

  useEffect(() => {
    setDraftValue({
      year: value.year,
      month: value.month,
      day: value.day,
    });
    setVisibleMonth({
      year: value.year,
      month: value.month,
    });
  }, [value.day, value.month, value.year]);

  const cancelSelection = () => {
    setDraftValue(value);
    setVisibleMonth({
      year: value.year,
      month: value.month,
    });
  };

  const confirmSelection = () => {
    if (confirmDisabled) {
      return;
    }

    onChange(draftValue);
  };

  return (
    <div className="date-calendar-group">
      <DateCalendarPanel
        visibleMonth={visibleMonth}
        onVisibleMonthChange={setVisibleMonth}
        locale={locale}
        ariaLabel={ariaLabel}
        selectedDate={displayedValue}
        showActions={showActions}
        disableFuture={disableFuture}
        confirmDisabled={confirmDisabled}
        onCancel={cancelSelection}
        onConfirm={confirmSelection}
        onDateSelect={showActions ? setDraftValue : onChange}
      />
      <span className="date-calendar-group__value">{formatCalendarDate(displayedValue, locale)}</span>
    </div>
  );
};

export const DatePicker = ({
  title,
  value,
  onChange,
  locale = 'ko',
  defaultOpen = false,
  disableFuture = true,
  confirmDisabled,
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [draftValue, setDraftValue] = useState(value);
  const [visibleMonth, setVisibleMonth] = useState<YearMonth>({
    year: value.year,
    month: value.month,
  });
  const rootRef = useOutsideClose(isOpen, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen) {
      setDraftValue({
        year: value.year,
        month: value.month,
        day: value.day,
      });
      setVisibleMonth({
        year: value.year,
        month: value.month,
      });
    }
  }, [isOpen, value.day, value.month, value.year]);

  const cancelSelection = () => {
    setDraftValue(value);
    setIsOpen(false);
  };

  const confirmSelection = () => {
    if (confirmDisabled) {
      return;
    }

    onChange(draftValue);
    setIsOpen(false);
  };

  return (
    <div ref={rootRef} className={`calendar-picker date-picker${isOpen ? ' is-open' : ''}`}>
      <strong>{title}</strong>
      <button type="button" className="calendar-trigger" onClick={() => setIsOpen((current) => !current)}>
        <span>{formatCalendarDate(value, locale)}</span>
        <CalendarIcon />
      </button>
      {isOpen && (
        <div className="calendar-picker__panel date-picker__panel">
          <DateCalendarPanel
            visibleMonth={visibleMonth}
            onVisibleMonthChange={setVisibleMonth}
            locale={locale}
            ariaLabel={title}
            selectedDate={draftValue}
            showActions
            disableFuture={disableFuture}
            confirmDisabled={confirmDisabled}
            onCancel={cancelSelection}
            onConfirm={confirmSelection}
            onDateSelect={setDraftValue}
          />
        </div>
      )}
    </div>
  );
};

export const MultiCalendar = ({
  value,
  onChange,
  locale = 'ko',
  disableFuture = false,
  confirmDisabled,
  onCancel,
  onConfirm,
}: MultiCalendarProps) => {
  const startRangeBase = value.start ?? getToday();
  const endRangeBase = value.end ?? value.start ?? getToday();
  const [startVisibleMonth, setStartVisibleMonth] = useState<YearMonth>({
    year: startRangeBase.year,
    month: startRangeBase.month,
  });
  const [endVisibleMonth, setEndVisibleMonth] = useState<YearMonth>({
    year: endRangeBase.year,
    month: endRangeBase.month,
  });
  const selectedDays = getRangeDays(value);
  const valueEndMonth = value.end?.month;
  const valueEndYear = value.end?.year;
  const valueStartMonth = value.start?.month;
  const valueStartYear = value.start?.year;
  const today = getToday();

  useEffect(() => {
    if (valueStartMonth && valueStartYear) {
      setStartVisibleMonth({
        year: valueStartYear,
        month: valueStartMonth,
      });
    }
  }, [valueStartMonth, valueStartYear]);

  useEffect(() => {
    if (valueEndMonth && valueEndYear) {
      setEndVisibleMonth({
        year: valueEndYear,
        month: valueEndMonth,
      });
      return;
    }

    if (valueStartMonth && valueStartYear) {
      setEndVisibleMonth({
        year: valueStartYear,
        month: valueStartMonth,
      });
    }
  }, [valueEndMonth, valueEndYear, valueStartMonth, valueStartYear]);

  const selectStartDate = (date: CalendarDate) => {
    if (
      (disableFuture && compareDate(date, today) > 0) ||
      (value.end && compareDate(date, value.end) > 0)
    ) {
      return;
    }

    onChange({
      start: date,
      end: value.end,
    });
  };

  const selectEndDate = (date: CalendarDate) => {
    if (
      !value.start ||
      (disableFuture && compareDate(date, today) > 0) ||
      compareDate(date, value.start) < 0
    ) {
      return;
    }

    onChange({
      start: value.start,
      end: date,
    });
  };

  const applyQuickRange = (range: CalendarDateRange) => {
    onChange(range);

    if (range.start) {
      setStartVisibleMonth({
        year: range.start.year,
        month: range.start.month,
      });
    }

    if (range.end) {
      setEndVisibleMonth({
        year: range.end.year,
        month: range.end.month,
      });
    }
  };

  const thisYearEnd = disableFuture
    ? today
    : { year: today.year, month: 12, day: 31 };
  const quickRanges = [
    {
      key: 'year',
      label: locale === 'ko' ? '올해' : 'This year',
      range: {
        start: { year: today.year, month: 1, day: 1 },
        end: thisYearEnd,
      },
    },
    {
      key: 'untilToday',
      label: locale === 'ko' ? '오늘까지' : 'Until today',
      range: {
        start: { year: today.year, month: 1, day: 1 },
        end: today,
      },
    },
    {
      key: 'today',
      label: locale === 'ko' ? '오늘' : 'Today',
      range: {
        start: today,
        end: today,
      },
    },
    {
      key: 'yesterday',
      label: locale === 'ko' ? '전일' : 'Yesterday',
      range: {
        start: addDays(today, -1),
        end: addDays(today, -1),
      },
    },
    {
      key: 'week',
      label: locale === 'ko' ? '주간' : 'Week',
      range: {
        start: addDays(today, -6),
        end: today,
      },
    },
    {
      key: 'lastWeek',
      label: locale === 'ko' ? '전주' : 'Last week',
      range: {
        start: addDays(today, -13),
        end: addDays(today, -7),
      },
    },
  ];
  const activeQuickKey =
    quickRanges.find((item) => isSameDateRange(value, item.range))?.key ?? '';

  return (
    <div className="multi-calendar">
      <div className="multi-calendar__quick">
        {quickRanges.map((item) => (
          <button
            type="button"
            className={activeQuickKey === item.key ? 'is-active' : undefined}
            key={item.key}
            onClick={() => applyQuickRange(item.range)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="multi-calendar__body">
        <div className="multi-calendar__months">
          <div className="multi-calendar__month">
            <span className="multi-calendar__month-label">
              {locale === 'ko' ? '시작' : 'Start'}
            </span>
            <DateCalendarPanel
              visibleMonth={startVisibleMonth}
              onVisibleMonthChange={setStartVisibleMonth}
              locale={locale}
              ariaLabel={locale === 'ko' ? '시작 날짜 Calendar' : 'Start date calendar'}
              range={value}
              disableFuture={disableFuture}
              maxDate={value.end ?? undefined}
              onDateSelect={selectStartDate}
            />
          </div>
          <div className="multi-calendar__month">
            <span className="multi-calendar__month-label">
              {locale === 'ko' ? '종료' : 'End'}
            </span>
            <DateCalendarPanel
              visibleMonth={endVisibleMonth}
              onVisibleMonthChange={setEndVisibleMonth}
              locale={locale}
              ariaLabel={locale === 'ko' ? '종료 날짜 Calendar' : 'End date calendar'}
              range={value}
              disableFuture={disableFuture}
              minDate={value.start ?? undefined}
              onDateSelect={selectEndDate}
            />
          </div>
        </div>
        <div className="multi-calendar__footer">
          <span>
            {locale === 'ko' ? '선택기간 ' : 'Selected period '}
            <strong>
              {selectedDays}
              {locale === 'ko' ? '일' : ' days'}
            </strong>
          </span>
          <div>
            <button type="button" className="lab-button lab-button--sm" onClick={onCancel}>
              {locale === 'ko' ? '취소' : 'Cancel'}
            </button>
            <button
              type="button"
              className="lab-button lab-button--sm lab-button--blue"
              disabled={confirmDisabled}
              onClick={onConfirm}
            >
              {locale === 'ko' ? '확인' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MultiDatePicker = ({
  title,
  value,
  onChange,
  locale = 'ko',
  disableFuture = false,
  confirmDisabled,
}: MultiDatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftRange, setDraftRange] = useState(value);
  const rootRef = useOutsideClose(isOpen, () => setIsOpen(false));
  const isConfirmDisabled = confirmDisabled || !draftRange.start || !draftRange.end;
  const valueEndDay = value.end?.day;
  const valueEndMonth = value.end?.month;
  const valueEndYear = value.end?.year;
  const valueStartDay = value.start?.day;
  const valueStartMonth = value.start?.month;
  const valueStartYear = value.start?.year;
  const controlledRange = useMemo<CalendarDateRange>(
    () => ({
      start: createOptionalCalendarDate(valueStartYear, valueStartMonth, valueStartDay),
      end: createOptionalCalendarDate(valueEndYear, valueEndMonth, valueEndDay),
    }),
    [
      valueEndDay,
      valueEndMonth,
      valueEndYear,
      valueStartDay,
      valueStartMonth,
      valueStartYear,
    ],
  );
  const displayedRange = isOpen ? draftRange : controlledRange;

  useEffect(() => {
    if (!isOpen) {
      setDraftRange(controlledRange);
    }
  }, [controlledRange, isOpen]);

  const openPicker = () => setIsOpen(true);

  const cancelSelection = () => {
    setDraftRange(controlledRange);
    setIsOpen(false);
  };

  const confirmSelection = () => {
    if (isConfirmDisabled) {
      return;
    }

    onChange(draftRange);
    setIsOpen(false);
  };

  return (
    <div ref={rootRef} className={`multi-date-picker${isOpen ? ' is-open' : ''}`}>
      {title && <strong>{title}</strong>}
      <div className="multi-date-picker__fields">
        <button
          type="button"
          className="calendar-trigger"
          aria-expanded={isOpen}
          aria-label={locale === 'ko' ? '시작 날짜 선택' : 'Select start date'}
          onClick={openPicker}
        >
          <span>{formatOptionalCalendarDate(displayedRange.start, locale)}</span>
          <CalendarIcon />
        </button>
        <span className="multi-date-picker__separator">~</span>
        <button
          type="button"
          className="calendar-trigger"
          aria-expanded={isOpen}
          aria-label={locale === 'ko' ? '종료 날짜 선택' : 'Select end date'}
          onClick={openPicker}
        >
          <span>{formatOptionalCalendarDate(displayedRange.end, locale)}</span>
          <CalendarIcon />
        </button>
      </div>
      {isOpen && (
        <div className="multi-date-picker__panel">
          <MultiCalendar
            value={draftRange}
            locale={locale}
            disableFuture={disableFuture}
            confirmDisabled={isConfirmDisabled}
            onChange={setDraftRange}
            onCancel={cancelSelection}
            onConfirm={confirmSelection}
          />
        </div>
      )}
    </div>
  );
};
